import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  BucketAcl,
  BucketCorsPolicy,
  BucketLifecyclePolicy,
  BucketLifecycleRule,
  BucketPolicy,
  ChangeBucketVisibilityParams,
  CheckCorsPolicyParams,
  CheckCorsPolicyResponse,
  CorsPolicy,
  CreateBucketParams,
  CreateBucketResponse,
  CreateCredentialsParams,
  CreateCredentialsResponse,
  CreateFolderParams,
  CreateSubUserKeyParams,
  CreateSubUserKeyResponse,
  GetSubUserKeyParams,
  CreateSubUserParams,
  CreateSubUserResponse,
  Credentials,
  DeleteBucketLifecycleRuleParams,
  DeleteBucketParams,
  DeleteCredentialsParams,
  DeleteFileAllVersionsParams,
  DeleteFileParams,
  DeleteFilesParams,
  DeleteSubUserKeyParams,
  DeleteSubUserParams,
  DownloadFileParams,
  DownloadFileResponse,
  FileVersions,
  GetBucketAclParams,
  GetBucketAclResponse,
  GetBucketCorsPolicyParams,
  GetBucketCorsPolicyResponse,
  GetBucketLifecyclePolicyParams,
  GetBucketLifecyclePolicyResponse,
  GetBucketLifecycleRuleParams,
  GetBucketLifecycleRuleResponse,
  GetBucketParams,
  GetBucketPolicyParams,
  GetBucketPolicyResponse,
  GetBucketResponse,
  GetBucketsParams,
  GetBucketsResponse,
  GetCredentialsListParams,
  GetCredentialsListResponse,
  GetCredentialsParams,
  GetCredentialsResponse,
  GetFileAllVersionsParams,
  GetFileAllVersionsResponse,
  GetFileSignedDownloadLinkParams,
  GetFileSignedDownloadLinkResponse,
  GetFilesParams,
  GetFilesResponse,
  GetFlatFilesParams,
  GetSubUsersParams,
  GetSubUsersResponse,
  RegenerateCredentialsParams,
  RegenerateCredentialsResponse,
  S3Bucket,
  S3FilesTotal,
  SetBucketLifecycleRuleParams,
  SetBucketLifecycleRuleResponse,
  SetCorsPolicyParams,
  SubUser,
  SubUserKey,
  UpdateVersioningStatusParams,
  UploadFileParams,
  UploadFileResponse
} from "./types";

export type ObjectStorageState = {
  credentialsList: Credentials[] | null;
  isCredentialsListLoading: boolean;
  credentials: Credentials | null;
  areCredentialsLoading: boolean;
  areCredentialsCreating: boolean;
  areCredentialsRegenerating: boolean;
  areCredentialsDeleting: boolean;
  bucketsList: S3Bucket[] | null;
  bucket: S3Bucket | null;
  isBucketLoading: boolean;
  isBucketsListLoading: boolean;
  isBucketCreating: boolean;
  isBucketDeleting: boolean;
  filesList: S3FilesTotal | null;
  isFilesListLoading: boolean;
  isFlatFilesListLoading: boolean;
  isFlatFilesListByPrefixLoading: boolean;
  isFlatFilesListRefreshing: boolean;
  isFileDeleting: boolean;
  isFileAllVersionsDeleting: boolean;
  fileAllVersions: FileVersions | null;
  isFileAllVersionsLoading: boolean;
  areFilesDeleting: boolean;
  isFileDownloading: boolean;
  ifFileSignedDownloadLinkGenerating: boolean;
  fileSignedDownloadLink: string;
  isFileUploading: boolean;
  isUploadSuccessful: boolean;
  uploadProgress: number;
  isBucketVisibilityChanging: boolean;
  corsPolicy: CorsPolicy | null;
  isCorsPolicyChecking: boolean;
  isCorsPolicySetting: boolean;
  isFolderCreating: boolean;
  bucketLifecycleRule: BucketLifecycleRule | null;
  isBucketLifecycleRuleLoading: boolean;
  isBucketLifecycleRuleSetting: boolean;
  isBucketLifecycleRuleDeleting: boolean;
  isVersioningStatusUpdating: boolean;
  bucketPolicy: BucketPolicy | null;
  isBucketPolicyLoading: boolean;
  bucketLifecyclePolicy: BucketLifecyclePolicy | null;
  isBucketLifecyclePolicyLoading: boolean;
  bucketCorsPolicy: BucketCorsPolicy | null;
  isBucketCorsPolicyLoading: boolean;
  bucketAcl: BucketAcl | null;
  isBucketAclLoading: boolean;
  subUsersList: SubUser[] | null;
  areSubUsersLoading: boolean;
  isSubUserCreating: boolean;
  isSubUserDeleting: boolean;
  isSubUserKeyCreating: boolean;
  isSubUserKeyDeleting: boolean;
  subUserKey: SubUserKey | null;
  isSubUserKeyLoading: boolean;
};

const initialState: ObjectStorageState = {
  credentialsList: null,
  isCredentialsListLoading: false,
  credentials: null,
  areCredentialsLoading: false,
  areCredentialsCreating: false,
  areCredentialsRegenerating: false,
  areCredentialsDeleting: false,
  bucketsList: null,
  bucket: null,
  isBucketLoading: false,
  isBucketsListLoading: false,
  isBucketCreating: false,
  isBucketDeleting: false,
  filesList: null,
  isFilesListLoading: false,
  isFlatFilesListLoading: false,
  isFlatFilesListByPrefixLoading: false,
  isFlatFilesListRefreshing: false,
  isFileDeleting: false,
  isFileAllVersionsDeleting: false,
  fileAllVersions: null,
  isFileAllVersionsLoading: false,
  areFilesDeleting: false,
  isFileDownloading: false,
  ifFileSignedDownloadLinkGenerating: false,
  fileSignedDownloadLink: "",
  isFileUploading: false,
  isUploadSuccessful: false,
  uploadProgress: 0,
  isBucketVisibilityChanging: false,
  corsPolicy: null,
  isCorsPolicyChecking: false,
  isCorsPolicySetting: false,
  isFolderCreating: false,
  bucketLifecycleRule: null,
  isBucketLifecycleRuleLoading: false,
  isBucketLifecycleRuleSetting: false,
  isBucketLifecycleRuleDeleting: false,
  isVersioningStatusUpdating: false,
  bucketPolicy: null,
  isBucketPolicyLoading: false,
  bucketLifecyclePolicy: null,
  isBucketLifecyclePolicyLoading: false,
  bucketCorsPolicy: null,
  isBucketCorsPolicyLoading: false,
  bucketAcl: null,
  isBucketAclLoading: false,
  subUsersList: null,
  areSubUsersLoading: false,
  isSubUserCreating: false,
  isSubUserDeleting: false,
  isSubUserKeyCreating: false,
  isSubUserKeyDeleting: false,
  subUserKey: null,
  isSubUserKeyLoading: false
};

const objectStorageSlice = createSlice({
  name: "objectStorage",
  initialState,
  reducers: {
    // Credentials
    getCredentialsListRequest: (
      state,
      action: PayloadAction<GetCredentialsListParams>
    ) => {
      state.isCredentialsListLoading = true;
    },
    getCredentialsListSuccess: (
      state,
      action: PayloadAction<GetCredentialsListResponse>
    ) => {
      state.credentialsList = action.payload;
      state.isCredentialsListLoading = false;
    },
    getCredentialsListFailure: (
      state,
      action: PayloadAction<{ params: GetCredentialsListParams; error: string }>
    ) => {
      state.isCredentialsListLoading = false;
    },

    getCredentialsRequest: (
      state,
      action: PayloadAction<GetCredentialsParams>
    ) => {
      state.areCredentialsLoading = true;
    },
    getCredentialsSuccess: (
      state,
      action: PayloadAction<GetCredentialsResponse>
    ) => {
      state.credentials = action.payload;
      state.areCredentialsLoading = false;
    },
    getCredentialsFailure: (
      state,
      action: PayloadAction<{ params: GetCredentialsParams; error: string }>
    ) => {
      state.areCredentialsLoading = false;
    },

    createCredentialsRequest: (
      state,
      action: PayloadAction<CreateCredentialsParams>
    ) => {
      state.areCredentialsCreating = true;
    },
    createCredentialsSuccess: (
      state,
      action: PayloadAction<CreateCredentialsResponse>
    ) => {
      state.credentials = action.payload;
      state.areCredentialsCreating = false;
    },
    createCredentialsFailure: (
      state,
      action: PayloadAction<{ params: CreateCredentialsParams; error: string }>
    ) => {
      state.areCredentialsCreating = false;
    },

    regenerateCredentialsRequest: (
      state,
      action: PayloadAction<RegenerateCredentialsParams>
    ) => {
      state.areCredentialsRegenerating = true;
    },
    regenerateCredentialsSuccess: (
      state,
      action: PayloadAction<RegenerateCredentialsResponse>
    ) => {
      state.credentials = action.payload;
      state.areCredentialsRegenerating = false;
    },
    regenerateCredentialsFailure: (
      state,
      action: PayloadAction<{
        params: RegenerateCredentialsParams;
        error: string;
      }>
    ) => {
      state.areCredentialsRegenerating = false;
    },

    deleteCredentialsRequest: (
      state,
      action: PayloadAction<DeleteCredentialsParams>
    ) => {
      state.areCredentialsDeleting = true;
    },
    deleteCredentialsSuccess: (state) => {
      state.areCredentialsDeleting = false;
    },
    deleteCredentialsFailure: (
      state,
      action: PayloadAction<{ params: DeleteCredentialsParams; error: string }>
    ) => {
      state.areCredentialsDeleting = false;
    },

    // Buckets
    getBucketsRequest: (state, action: PayloadAction<GetBucketsParams>) => {
      state.isBucketsListLoading = true;
    },
    getBucketsSuccess: (state, action: PayloadAction<GetBucketsResponse>) => {
      state.bucketsList = action.payload;
      state.isBucketsListLoading = false;
    },
    getBucketsFailure: (
      state,
      action: PayloadAction<{ params: GetBucketsParams; error: string }>
    ) => {
      state.isBucketsListLoading = false;
    },

    getBucketRequest: (state, action: PayloadAction<GetBucketParams>) => {
      state.isBucketLoading = true;
    },
    getBucketSuccess: (state, action: PayloadAction<GetBucketResponse>) => {
      state.bucket = action.payload;
      state.isBucketLoading = false;
    },
    getBucketFailure: (
      state,
      action: PayloadAction<{ params: GetBucketParams; error: string }>
    ) => {
      state.isBucketLoading = false;
    },

    createBucketRequest: (state, action: PayloadAction<CreateBucketParams>) => {
      state.isBucketCreating = true;
    },
    createBucketSuccess: (
      state,
      action: PayloadAction<CreateBucketResponse>
    ) => {
      state.isBucketCreating = false;
    },
    createBucketFailure: (
      state,
      action: PayloadAction<{ params: CreateBucketParams; error: string }>
    ) => {
      state.isBucketCreating = false;
    },

    deleteBucketRequest: (state, action: PayloadAction<DeleteBucketParams>) => {
      state.isBucketDeleting = true;
    },
    deleteBucketSuccess: (state) => {
      state.isBucketDeleting = false;
      state.bucket = null;
    },
    deleteBucketFailure: (
      state,
      action: PayloadAction<{ params: DeleteBucketParams; error: string }>
    ) => {
      state.isBucketDeleting = false;
    },

    changeBucketVisibilityRequest: (
      state,
      action: PayloadAction<ChangeBucketVisibilityParams>
    ) => {
      state.isBucketVisibilityChanging = true;
    },
    changeBucketVisibilitySuccess: (state) => {
      state.isBucketVisibilityChanging = false;
    },
    changeBucketVisibilityFailure: (
      state,
      action: PayloadAction<{
        params: ChangeBucketVisibilityParams;
        error: string;
      }>
    ) => {
      state.isBucketVisibilityChanging = false;
    },

    // Bucket lifecycle rule
    getBucketLifecycleRuleRequest: (
      state,
      action: PayloadAction<GetBucketLifecycleRuleParams>
    ) => {
      state.isBucketLifecycleRuleLoading = true;
    },
    getBucketLifecycleRuleSuccess: (
      state,
      action: PayloadAction<GetBucketLifecycleRuleResponse>
    ) => {
      state.bucketLifecycleRule = action.payload;
      state.isBucketLifecycleRuleLoading = false;
    },
    getBucketLifecycleRuleFailure: (
      state,
      action: PayloadAction<{
        params: GetBucketLifecycleRuleParams;
        error: string;
      }>
    ) => {
      state.isBucketLifecycleRuleLoading = false;
    },

    setBucketLifecycleRuleRequest: (
      state,
      action: PayloadAction<SetBucketLifecycleRuleParams>
    ) => {
      state.isBucketLifecycleRuleSetting = true;
    },
    setBucketLifecycleRuleSuccess: (
      state,
      action: PayloadAction<SetBucketLifecycleRuleResponse>
    ) => {
      state.isBucketLifecycleRuleSetting = false;
    },
    setBucketLifecycleRuleFailure: (
      state,
      action: PayloadAction<{
        params: SetBucketLifecycleRuleParams;
        error: string;
      }>
    ) => {
      state.isBucketLifecycleRuleSetting = false;
    },

    deleteBucketLifecycleRuleRequest: (
      state,
      action: PayloadAction<DeleteBucketLifecycleRuleParams>
    ) => {
      state.isBucketLifecycleRuleDeleting = true;
    },
    deleteBucketLifecycleRuleSuccess: (state) => {
      state.isBucketLifecycleRuleDeleting = false;
    },
    deleteBucketLifecycleRuleFailure: (
      state,
      action: PayloadAction<{
        params: DeleteBucketLifecycleRuleParams;
        error: string;
      }>
    ) => {
      state.isBucketLifecycleRuleDeleting = false;
    },

    /*
      Bucket policies.

      Every policy is a separate request, and a bucket that has none of them is
      a perfectly normal bucket, so a failure only resets the policy instead of
      raising an error - the selector then lists that type as not configured.
    */
    getBucketPolicyRequest: (
      state,
      action: PayloadAction<GetBucketPolicyParams>
    ) => {
      state.isBucketPolicyLoading = true;
    },
    getBucketPolicySuccess: (
      state,
      action: PayloadAction<GetBucketPolicyResponse>
    ) => {
      state.bucketPolicy = action.payload;
      state.isBucketPolicyLoading = false;
    },
    getBucketPolicyFailure: (
      state,
      action: PayloadAction<{ params: GetBucketPolicyParams; error: string }>
    ) => {
      state.isBucketPolicyLoading = false;
    },

    getBucketLifecyclePolicyRequest: (
      state,
      action: PayloadAction<GetBucketLifecyclePolicyParams>
    ) => {
      state.isBucketLifecyclePolicyLoading = true;
    },
    getBucketLifecyclePolicySuccess: (
      state,
      action: PayloadAction<GetBucketLifecyclePolicyResponse>
    ) => {
      state.bucketLifecyclePolicy = action.payload;
      state.isBucketLifecyclePolicyLoading = false;
    },
    getBucketLifecyclePolicyFailure: (
      state,
      action: PayloadAction<{
        params: GetBucketLifecyclePolicyParams;
        error: string;
      }>
    ) => {
      state.isBucketLifecyclePolicyLoading = false;
    },

    getBucketCorsPolicyRequest: (
      state,
      action: PayloadAction<GetBucketCorsPolicyParams>
    ) => {
      state.isBucketCorsPolicyLoading = true;
    },
    getBucketCorsPolicySuccess: (
      state,
      action: PayloadAction<GetBucketCorsPolicyResponse>
    ) => {
      state.bucketCorsPolicy = action.payload;
      state.isBucketCorsPolicyLoading = false;
    },
    getBucketCorsPolicyFailure: (
      state,
      action: PayloadAction<{
        params: GetBucketCorsPolicyParams;
        error: string;
      }>
    ) => {
      state.isBucketCorsPolicyLoading = false;
    },

    getBucketAclRequest: (state, action: PayloadAction<GetBucketAclParams>) => {
      state.isBucketAclLoading = true;
    },
    getBucketAclSuccess: (
      state,
      action: PayloadAction<GetBucketAclResponse>
    ) => {
      state.bucketAcl = action.payload;
      state.isBucketAclLoading = false;
    },
    getBucketAclFailure: (
      state,
      action: PayloadAction<{ params: GetBucketAclParams; error: string }>
    ) => {
      state.isBucketAclLoading = false;
    },

    // Files
    getFilesRequest: (state, action: PayloadAction<GetFilesParams>) => {
      state.isFilesListLoading = true;
    },
    getFilesSuccess: (state, action: PayloadAction<GetFilesResponse>) => {
      state.filesList = action.payload;
      state.isFilesListLoading = false;
    },
    getFilesFailure: (
      state,
      action: PayloadAction<{ params: GetFilesParams; error: string }>
    ) => {
      state.isFilesListLoading = false;
    },

    getFlatFilesRequest: (state, action: PayloadAction<GetFlatFilesParams>) => {
      state.isFlatFilesListLoading = true;
    },
    getFlatFilesSuccess: (state, action: PayloadAction<GetFilesResponse>) => {
      state.filesList = action.payload;
      state.isFlatFilesListLoading = false;
    },
    getFlatFilesFailure: (
      state,
      action: PayloadAction<{ params: GetFlatFilesParams; error: string }>
    ) => {
      state.isFlatFilesListLoading = false;
    },

    getFlatFilesByPrefixRequest: (
      state,
      action: PayloadAction<GetFlatFilesParams>
    ) => {
      state.isFlatFilesListByPrefixLoading = true;
    },
    getFlatFilesByPrefixSuccess: (
      state,
      action: PayloadAction<GetFilesResponse>
    ) => {
      state.filesList = action.payload;
      state.isFlatFilesListByPrefixLoading = false;
    },
    getFlatFilesByPrefixFailure: (
      state,
      action: PayloadAction<{ params: GetFlatFilesParams; error: string }>
    ) => {
      state.isFlatFilesListByPrefixLoading = false;
    },

    getFlatFilesRefreshedRequest: (
      state,
      action: PayloadAction<GetFlatFilesParams>
    ) => {
      state.isFlatFilesListRefreshing = true;
    },
    getFlatFilesRefreshedSuccess: (
      state,
      action: PayloadAction<GetFilesResponse>
    ) => {
      state.filesList = action.payload;
      state.isFlatFilesListRefreshing = false;
    },
    getFlatFilesRefreshedFailure: (
      state,
      action: PayloadAction<{ params: GetFlatFilesParams; error: string }>
    ) => {
      state.isFlatFilesListRefreshing = false;
    },

    clearFiles: (state) => {
      state.filesList = null;
      state.isFilesListLoading = true;
    },

    deleteFileRequest: (state, action: PayloadAction<DeleteFileParams>) => {
      state.isFileDeleting = true;
    },
    deleteFileSuccess: (state) => {
      state.isFileDeleting = false;
    },
    deleteFileFailure: (
      state,
      action: PayloadAction<{ params: DeleteFileParams; error: string }>
    ) => {
      state.isFileDeleting = false;
    },

    deleteFileAllVersionsRequest: (
      state,
      action: PayloadAction<DeleteFileAllVersionsParams>
    ) => {
      state.isFileAllVersionsDeleting = true;
    },
    deleteFileAllVersionsSuccess: (state) => {
      state.isFileAllVersionsDeleting = false;
    },
    deleteFileAllVersionsFailure: (
      state,
      action: PayloadAction<{
        params: DeleteFileAllVersionsParams;
        error: string;
      }>
    ) => {
      state.isFileAllVersionsDeleting = false;
    },

    getFileAllVersionsRequest: (
      state,
      action: PayloadAction<GetFileAllVersionsParams>
    ) => {
      state.isFileAllVersionsLoading = true;
    },
    getFileAllVersionsSuccess: (
      state,
      action: PayloadAction<GetFileAllVersionsResponse>
    ) => {
      state.fileAllVersions = action.payload;
      state.isFileAllVersionsLoading = false;
    },
    getFileAllVersionsFailure: (
      state,
      action: PayloadAction<{
        params: GetFileAllVersionsParams;
        error: string;
      }>
    ) => {
      state.isFileAllVersionsLoading = false;
    },

    deleteFilesRequest: (state, action: PayloadAction<DeleteFilesParams>) => {
      state.areFilesDeleting = true;
    },
    deleteFilesSuccess: (state) => {
      state.areFilesDeleting = false;
    },
    deleteFilesFailure: (
      state,
      action: PayloadAction<{ params: DeleteFilesParams; error: string }>
    ) => {
      state.areFilesDeleting = false;
    },

    getFileSignedDownloadLinkRequest: (
      state,
      action: PayloadAction<GetFileSignedDownloadLinkParams>
    ) => {
      state.ifFileSignedDownloadLinkGenerating = true;
    },
    getFileSignedDownloadLinkSuccess: (
      state,
      action: PayloadAction<GetFileSignedDownloadLinkResponse>
    ) => {
      state.fileSignedDownloadLink = action.payload;
      state.ifFileSignedDownloadLinkGenerating = false;
    },
    getFileSignedDownloadLinkFailure: (
      state,
      action: PayloadAction<{
        params: GetFileSignedDownloadLinkParams;
        error: string;
      }>
    ) => {
      state.ifFileSignedDownloadLinkGenerating = false;
    },

    downloadFileRequest: (state, action: PayloadAction<DownloadFileParams>) => {
      state.isFileDownloading = true;
    },
    downloadFileSuccess: (
      state,
      action: PayloadAction<DownloadFileResponse>
    ) => {
      state.isFileDownloading = false;
    },
    downloadFileFailure: (
      state,
      action: PayloadAction<{ params: DownloadFileParams; error: string }>
    ) => {
      state.isFileDownloading = false;
    },

    // Upload
    uploadFileRequest: (state, action: PayloadAction<UploadFileParams>) => {
      state.isFileUploading = true;
      state.isUploadSuccessful = false;
    },
    uploadFileSuccess: (state, action: PayloadAction<UploadFileResponse>) => {
      state.isFileUploading = false;
      state.isUploadSuccessful = true;
      state.uploadProgress = 0;
    },
    uploadFileFailure: (
      state,
      action: PayloadAction<{ params: UploadFileParams; error: string }>
    ) => {
      state.isFileUploading = false;
      state.isUploadSuccessful = false;
      state.uploadProgress = 0;
    },
    uploadProgress: (state, action: PayloadAction<number>) => {
      state.uploadProgress = action.payload;
    },

    // CORS policy
    checkCorsPolicyRequest: (
      state,
      action: PayloadAction<CheckCorsPolicyParams>
    ) => {
      state.isCorsPolicyChecking = true;
    },
    checkCorsPolicySuccess: (
      state,
      action: PayloadAction<CheckCorsPolicyResponse>
    ) => {
      state.corsPolicy = action.payload;
      state.isCorsPolicyChecking = false;
    },
    checkCorsPolicyFailure: (
      state,
      action: PayloadAction<{ params: CheckCorsPolicyParams; error: string }>
    ) => {
      state.isCorsPolicyChecking = false;
    },

    setCorsPolicyRequest: (
      state,
      action: PayloadAction<SetCorsPolicyParams>
    ) => {
      state.isCorsPolicySetting = true;
    },
    setCorsPolicySuccess: (state) => {
      state.isCorsPolicySetting = false;
    },
    setCorsPolicyFailure: (
      state,
      action: PayloadAction<{ params: SetCorsPolicyParams; error: string }>
    ) => {
      state.isCorsPolicySetting = false;
    },

    // Folder
    createFolderRequest: (state, action: PayloadAction<CreateFolderParams>) => {
      state.isFolderCreating = true;
    },
    createFolderSuccess: (state) => {
      state.isFolderCreating = false;
    },
    createFolderFailure: (
      state,
      action: PayloadAction<{ params: CreateFolderParams; error: string }>
    ) => {
      state.isFolderCreating = false;
    },

    // Versioning
    updateVersioningStatusRequest: (
      state,
      action: PayloadAction<UpdateVersioningStatusParams>
    ) => {
      state.isVersioningStatusUpdating = true;
    },
    updateVersioningStatusSuccess: (state) => {
      state.isVersioningStatusUpdating = false;
    },
    updateVersioningStatusFailure: (
      state,
      action: PayloadAction<{
        params: UpdateVersioningStatusParams;
        error: string;
      }>
    ) => {
      state.isVersioningStatusUpdating = false;
    },

    // Subusers
    getSubUsersRequest: (state, action: PayloadAction<GetSubUsersParams>) => {
      state.areSubUsersLoading = true;
    },
    getSubUsersSuccess: (state, action: PayloadAction<GetSubUsersResponse>) => {
      state.subUsersList = action.payload;
      state.areSubUsersLoading = false;
    },
    getSubUsersFailure: (
      state,
      action: PayloadAction<{ params: GetSubUsersParams; error: string }>
    ) => {
      state.areSubUsersLoading = false;
    },

    createSubUserRequest: (
      state,
      action: PayloadAction<CreateSubUserParams>
    ) => {
      state.isSubUserCreating = true;
    },
    createSubUserSuccess: (
      state,
      action: PayloadAction<CreateSubUserResponse>
    ) => {
      state.isSubUserCreating = false;
    },
    createSubUserFailure: (
      state,
      action: PayloadAction<{ params: CreateSubUserParams; error: string }>
    ) => {
      state.isSubUserCreating = false;
    },

    deleteSubUserRequest: (
      state,
      action: PayloadAction<DeleteSubUserParams>
    ) => {
      state.isSubUserDeleting = true;
    },
    deleteSubUserSuccess: (state) => {
      state.isSubUserDeleting = false;
    },
    deleteSubUserFailure: (
      state,
      action: PayloadAction<{ params: DeleteSubUserParams; error: string }>
    ) => {
      state.isSubUserDeleting = false;
    },

    getSubUserKeyRequest: (
      state,
      action: PayloadAction<GetSubUserKeyParams>
    ) => {
      state.isSubUserKeyLoading = true;
    },
    getSubUserKeySuccess: (state, action: PayloadAction<SubUserKey>) => {
      state.subUserKey = action.payload;
      state.isSubUserKeyLoading = false;
    },
    getSubUserKeyFailure: (
      state,
      action: PayloadAction<{ params: GetSubUserKeyParams; error: string }>
    ) => {
      state.isSubUserKeyLoading = false;
    },

    createSubUserKeyRequest: (
      state,
      action: PayloadAction<CreateSubUserKeyParams>
    ) => {
      state.isSubUserKeyCreating = true;
    },
    createSubUserKeySuccess: (
      state,
      action: PayloadAction<CreateSubUserKeyResponse>
    ) => {
      state.isSubUserKeyCreating = false;
    },
    createSubUserKeyFailure: (
      state,
      action: PayloadAction<{ params: CreateSubUserKeyParams; error: string }>
    ) => {
      state.isSubUserKeyCreating = false;
    },

    deleteSubUserKeyRequest: (
      state,
      action: PayloadAction<DeleteSubUserKeyParams>
    ) => {
      state.isSubUserKeyDeleting = true;
    },
    deleteSubUserKeySuccess: (state) => {
      state.isSubUserKeyDeleting = false;
    },
    deleteSubUserKeyFailure: (
      state,
      action: PayloadAction<{ params: DeleteSubUserKeyParams; error: string }>
    ) => {
      state.isSubUserKeyDeleting = false;
    },

    // Clear helpers
    clearCredentials: (state) => {
      state.credentials = null;
    },
    clearSubUsers: (state) => {
      state.subUsersList = null;
    },
    clearSubUserKey: (state) => {
      state.subUserKey = null;
    },
    clearFileAllVersions: (state) => {
      state.fileAllVersions = null;
    },
    clear: (state) => ({
      ...initialState,
      isFileUploading: state.isFileUploading,
      uploadProgress: state.uploadProgress
    })
  }
});

export const {
  getCredentialsListRequest,
  getCredentialsListSuccess,
  getCredentialsListFailure,
  getCredentialsRequest,
  getCredentialsSuccess,
  getCredentialsFailure,
  createCredentialsRequest,
  createCredentialsSuccess,
  createCredentialsFailure,
  regenerateCredentialsRequest,
  regenerateCredentialsSuccess,
  regenerateCredentialsFailure,
  deleteCredentialsRequest,
  deleteCredentialsSuccess,
  deleteCredentialsFailure,
  getBucketsRequest,
  getBucketsSuccess,
  getBucketsFailure,
  getBucketRequest,
  getBucketSuccess,
  getBucketFailure,
  createBucketRequest,
  createBucketSuccess,
  createBucketFailure,
  deleteBucketRequest,
  deleteBucketSuccess,
  deleteBucketFailure,
  changeBucketVisibilityRequest,
  changeBucketVisibilitySuccess,
  changeBucketVisibilityFailure,
  getBucketLifecycleRuleRequest,
  getBucketLifecycleRuleSuccess,
  getBucketLifecycleRuleFailure,
  setBucketLifecycleRuleRequest,
  setBucketLifecycleRuleSuccess,
  setBucketLifecycleRuleFailure,
  deleteBucketLifecycleRuleRequest,
  deleteBucketLifecycleRuleSuccess,
  deleteBucketLifecycleRuleFailure,
  getFilesRequest,
  getFilesSuccess,
  getFilesFailure,
  getFlatFilesRequest,
  getFlatFilesSuccess,
  getFlatFilesFailure,
  getFlatFilesByPrefixRequest,
  getFlatFilesByPrefixSuccess,
  getFlatFilesByPrefixFailure,
  getFlatFilesRefreshedRequest,
  getFlatFilesRefreshedSuccess,
  getFlatFilesRefreshedFailure,
  clearFiles,
  deleteFileRequest,
  deleteFileSuccess,
  deleteFileFailure,
  deleteFileAllVersionsRequest,
  deleteFileAllVersionsSuccess,
  deleteFileAllVersionsFailure,
  getFileAllVersionsRequest,
  getFileAllVersionsSuccess,
  getFileAllVersionsFailure,
  deleteFilesRequest,
  deleteFilesSuccess,
  deleteFilesFailure,
  getFileSignedDownloadLinkRequest,
  getFileSignedDownloadLinkSuccess,
  getFileSignedDownloadLinkFailure,
  downloadFileRequest,
  downloadFileSuccess,
  downloadFileFailure,
  uploadFileRequest,
  uploadFileSuccess,
  uploadFileFailure,
  uploadProgress,
  checkCorsPolicyRequest,
  checkCorsPolicySuccess,
  checkCorsPolicyFailure,
  setCorsPolicyRequest,
  setCorsPolicySuccess,
  setCorsPolicyFailure,
  createFolderRequest,
  createFolderSuccess,
  createFolderFailure,
  updateVersioningStatusRequest,
  updateVersioningStatusSuccess,
  updateVersioningStatusFailure,
  getSubUsersRequest,
  getSubUsersSuccess,
  getSubUsersFailure,
  createSubUserRequest,
  createSubUserSuccess,
  createSubUserFailure,
  deleteSubUserRequest,
  deleteSubUserSuccess,
  deleteSubUserFailure,
  getSubUserKeyRequest,
  getSubUserKeySuccess,
  getSubUserKeyFailure,
  createSubUserKeyRequest,
  createSubUserKeySuccess,
  createSubUserKeyFailure,
  deleteSubUserKeyRequest,
  deleteSubUserKeySuccess,
  deleteSubUserKeyFailure,
  clearCredentials,
  clearSubUsers,
  clearSubUserKey,
  clearFileAllVersions,
  clear
} = objectStorageSlice.actions;

export const { actions: objectStorageActions } = objectStorageSlice;

export const reducer = objectStorageSlice.reducer;
